import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import CustomerCard from "./components/CustomerCard";
import OutstandingCard from "./components/OutstandingCard";
import PaymentDrawer from "./components/payment/PaymentDrawer";
import LedgerDetailTable from "./components/LedgerDetailTable";
import { useLedgerFetch } from "./hooks/useLedgerFetch";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CommonButtons from "src/shared/components/CommonButtons";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import api from "src/features/auth/axiosConfig";
import { useUser } from "src/business-types/grocery/components/UserContext";
import "../../ledgers/Ledger.css";
import LedgerTabHeader from "./components/LedgerTabHeader";

interface PaginationInfo {
  currentPage: number; // 1-based
  pageSize: number;
  searchTerm: string;
  totalRecords: number;
}

const LedgerData: React.FC = () => {
  const { CustomerId = "" } = useParams<{ CustomerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const passedCustomers = location.state?.customers || [];
  const paginationInfo: PaginationInfo = location.state?.paginationInfo || {
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",
    totalRecords: 0,
  };

  const fromPage = location.state?.fromPage || 1;
  const fromPageSize = location.state?.fromPageSize || 10;
  const fromSearchTerm = location.state?.searchTerm || "";

  const [searchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("pageNumber")) || 1;

  const [page, setPage] = useState(initialPage - 1);
  const { userConfigration } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefultSize || paginationInfo.pageSize);

  const [openPayment, setOpenPayment] = useState(false);

  /** 🔥 PAGE CACHE (key = API pageNumber) */
  const [customerPages, setCustomerPages] = useState<Map<number, any[]>>(new Map());
  const [loadingNav, setLoadingNav] = useState(false);
  const [activeTab, setActiveTab] = useState<"Active" | "History">("Active");
  const includeHistory = activeTab === "History";
  const [sortField, setSortField] = useState<"Orderdate" | string>("Orderdate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      // toggle sort order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc"); // default to ascending when changing column
    }

    // reset to first page when sorting changes
    setPage(0);
  };

  const { customerData, paginationData, loading, refetch } = useLedgerFetch(CustomerId, page, rowsPerPage, includeHistory, sortField, sortOrder);

  /** INIT cache with clicked page */
  useEffect(() => {
    document.title = "Ledger › Details › iMetanic";
    if (passedCustomers.length > 0) {
      setCustomerPages(new Map([[paginationInfo.currentPage, passedCustomers]]));
    }
  }, [passedCustomers, paginationInfo.currentPage]);

  /** 🔁 Fetch any page on demand */
  const fetchPage = async (pageNumber: number) => {
    if (customerPages.has(pageNumber)) {
      return customerPages.get(pageNumber)!;
    }

    setLoadingNav(true);
    try {
      const res = await api.get("/pos/ledger", {
        params: {
          pageNumber,
          pageSize: paginationInfo.pageSize,
          searchBy: paginationInfo.searchTerm,
        },
      });

      const data = res.data.result || [];
      setCustomerPages((prev) => new Map(prev).set(pageNumber, data));
      return data;
    } catch {
      toast.error("Failed to load customers");
      return [];
    } finally {
      setLoadingNav(false);
    }
  };

  /** 🔎 Find current customer position */
  const findPosition = () => {
    const pages = Array.from(customerPages.entries()).sort((a, b) => a[0] - b[0]);
    let globalIndex = 0;

    for (const [pageNum, customers] of pages) {
      const index = customers.findIndex((c: any) => c.customer_id === CustomerId);

      if (index !== -1) {
        return {
          pageNum,
          index,
          globalIndex: globalIndex + index,
          nextId: customers[index + 1]?.customer_id ?? null,
          prevId: customers[index - 1]?.customer_id ?? null,
        };
      }
      globalIndex += customers.length;
    }

    return {
      pageNum: paginationInfo.currentPage,
      index: -1,
      globalIndex: -1,
      nextId: null,
      prevId: null,
    };
  };

  const { pageNum, index, globalIndex, nextId, prevId } = findPosition();

  /** ▶ NEXT */
  const goNext = async () => {
    if (nextId) {
      navigateCustomer(nextId, pageNum);
      return;
    }

    const nextPage = pageNum + 1;
    const data = await fetchPage(nextPage);

    if (data.length > 0) {
      navigateCustomer(data[0].customer_id, nextPage);
    } else {
      toast.info("No more customers");
    }
  };

  /** ◀ PREVIOUS */
  const goPrevious = async () => {
    if (prevId) {
      navigateCustomer(prevId, pageNum);
      return;
    }

    const prevPage = pageNum - 1;
    if (prevPage < 1) return;

    const data = await fetchPage(prevPage);
    if (data.length > 0) {
      navigateCustomer(data[data.length - 1].customer_id, prevPage);
    }
  };

  const navigateCustomer = (customerId: string, pageNumber: number) => {
    navigate(`/v1/ledger/${customerId}`, {
      state: {
        paginationInfo: { ...paginationInfo, currentPage: pageNumber },
        fromPage,
        fromPageSize,
        searchTerm: fromSearchTerm,
      },
      replace: true,
    });
  };

  const handleBack = () => {
    let url = `/v1/ledger?pageNumber=${fromPage}&pageSize=${fromPageSize}`;
    if (fromSearchTerm) url += `&searchBy=${encodeURIComponent(fromSearchTerm)}`;

    navigate(url, { state: { preserveSearch: true } });
  };

  const totalOutstanding = useMemo(() => {
    if (!customerData?.ledger) return "0.00";
    return customerData.ledger.reduce((s, l) => s + parseFloat(l.total_outstanding_balance || "0"), 0).toFixed(2);
  }, [customerData]);

  return (
    <div className="ledger-detail-page">
      <PageTitle title="- Ledger › Details" />

      {(loading || loadingNav) && <LoadingScreen />}

      {!loading && (
        <>
          <div className="ledger-detail-header">
            <CommonButtons label="BACK TO LIST" onClick={handleBack} variant="secondary" icon={KeyboardReturnIcon} />

            <h1 className="ledger-detail-title">Outstanding Balance Details of Customer</h1>

            {/* <span className="text-sm bg-gray-100 px-3 py-1 rounded">
              Customer {globalIndex + 1} of {paginationInfo.totalRecords}
            </span> */}

            <div className="flex gap-2">
              <CommonButtons label="Previous" variant="outlineDanger" onClick={goPrevious} disabled={loadingNav || (pageNum === 1 && index === 0)} />
              <CommonButtons label="Next" variant="primary" onClick={goNext} disabled={loadingNav} className="w-28" />
            </div>
          </div>

          <div className="ledger-detail-cards">
            {customerData && <CustomerCard customer={customerData} />}
            <OutstandingCard balance={totalOutstanding} onPay={() => setOpenPayment(true)} onRefund={() => {}} />
          </div>

          <div className="ml-4">
            <LedgerTabHeader activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>

          {customerData && (
            <LedgerDetailTable
              ledger={customerData.ledger}
              page={page}
              rowsPerPage={rowsPerPage}
              paginationData={paginationData}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              navigate={navigate}
              includeHistory={includeHistory}
              onSortChange={handleSortChange}
              sortField={sortField}
              sortOrder={sortOrder}
            />
          )}
        </>
      )}

      {openPayment && customerData && <PaymentDrawer onClose={() => setOpenPayment(false)} customerData={customerData} customerId={CustomerId} refetchLedger={refetch} />}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default LedgerData;
